import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CalendarClock, ChevronRight, FileSpreadsheet, Search, UserPlus, UserRound, Users, X } from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { Encabezado, Pagina } from '../components/Layout'
import { SubNavVentas } from '../components/SubNavVentas'
import { Avatar, Boton, Chip, Tarjeta, Vacio } from '../components/ui/base'
import { FormularioCliente } from '../components/FormularioCliente'
import { exportarClientesExcel } from '../export/excel'
import { cn } from '../lib/cn'
import { money, normalizar, relativo } from '../lib/format'
import { rankingClientes, vigentes } from '../lib/metricas'
import { RUBROS, type TipoCliente } from '../types'

function tipoDeParam(v: string | null): TipoCliente | null {
  return v === 'empresa' || v === 'particular' ? v : null
}

export function Clientes() {
  const { clientes, ventas } = useDatos()
  const navegar = useNavigate()
  const usuario = useSesion((s) => s.usuario)
  const avisar = useUI((s) => s.avisar)
  const esAdmin = usuario?.rol === 'admin'
  const [params] = useSearchParams()
  // Cuando se llega desde "Nueva venta" (botón Empresa/Particular), esta
  // misma pantalla hace de selector: se salta la portada, va directo a
  // la lista de ese grupo, y elegir un cliente vuelve a la venta en vez
  // de abrir su ficha.
  const paraVenta = tipoDeParam(params.get('paraVenta'))
  const [vista, setVista] = useState<TipoCliente | null>(paraVenta)
  const [texto, setTexto] = useState('')
  const [nuevo, setNuevo] = useState(false)

  useEffect(() => {
    if (paraVenta) setVista(paraVenta)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paraVenta])

  const stats = useMemo(() => {
    const mapa = new Map(rankingClientes(ventas).map((r) => [r.clienteId, r]))
    return mapa
  }, [ventas])

  /** Quién atiende a cada cliente: el vendedor de su venta más reciente.
   *  No hay un "vendedor asignado" guardado en la ficha —y ponerlo sería
   *  otra verdad más que mantener al día—, así que se deduce de los
   *  hechos: el último que le vendió es el que lo atiende. Es la misma
   *  regla que ya usa el mapa de la bitácora. */
  const vendedorPorCliente = useMemo(() => {
    const mapa = new Map<string, { nombre: string; fecha: string }>()
    for (const v of vigentes(ventas)) {
      if (!v.usuarioNombre) continue
      const actual = mapa.get(v.clienteId)
      if (!actual || v.fecha > actual.fecha) {
        mapa.set(v.clienteId, { nombre: v.usuarioNombre, fecha: v.fecha })
      }
    }
    return mapa
  }, [ventas])

  const empresas = clientes.filter((c) => c.tipo === 'empresa')
  const particulares = clientes.filter((c) => c.tipo === 'particular')

  const lista = useMemo(() => {
    if (!vista) return []
    const q = normalizar(texto.trim())
    return clientes
      .filter((c) => c.tipo === vista)
      .filter(
        (c) =>
          !q ||
          [c.nombre, c.apellido, c.rut, c.comuna, c.contacto, c.email, c.telefono].some(
            (campo) => campo && normalizar(campo).includes(q),
          ),
      )
      .sort((a, b) => {
        const va = stats.get(a.id)?.total ?? 0
        const vb = stats.get(b.id)?.total ?? 0
        if (va !== vb) return vb - va
        return a.nombre.localeCompare(b.nombre, 'es')
      })
  }, [clientes, vista, texto, stats])

  const descargarExcel = async () => {
    await exportarClientesExcel(clientes, ventas)
    avisar('Clientes descargados')
  }

  /* -------- Portada: los dos grupos, como pidió el flujo -------- */
  if (!vista) {
    return (
      <Pagina>
        <SubNavVentas activa="clientes" />
        <Encabezado
          atras
          titulo="Clientes"
          detalle={`${clientes.length} en total · ${money(ventas.reduce((s, v) => s + v.total, 0))} vendidos históricamente`}
          accion={
            <div className="flex flex-wrap gap-2">
              {esAdmin && (
                <Boton
                  variante="secundario"
                  icono={<FileSpreadsheet className="size-[18px]" />}
                  onClick={() => void descargarExcel()}
                >
                  Excel
                </Boton>
              )}
              <Boton icono={<UserPlus className="size-[18px]" />} onClick={() => setNuevo(true)}>
                Nuevo cliente
              </Boton>
            </div>
          }
        />

        <div className="grid gap-4 escalonar sm:grid-cols-2">
          <PuertaGrupo
            titulo="Empresas"
            detalle="Pastelerías, restaurantes, cafeterías, hoteles y distribuidores"
            cantidad={empresas.length}
            total={empresas.reduce((s, c) => s + (stats.get(c.id)?.total ?? 0), 0)}
            icono={<img src="/icono-empresa.jpg" alt="" className="size-full object-cover" />}
            tono="from-tinta-500 to-tinta-700"
            onClick={() => setVista('empresa')}
          />
          <PuertaGrupo
            titulo="Clientes particulares"
            detalle="Ventas directas a personas"
            cantidad={particulares.length}
            total={particulares.reduce((s, c) => s + (stats.get(c.id)?.total ?? 0), 0)}
            icono={<img src="/icono-particular.jpg" alt="" className="size-full object-cover" />}
            tono="from-magenta-500 to-magenta-700"
            onClick={() => setVista('particular')}
          />
        </div>

        {/* Los que más compran, como atajo a su ficha. */}
        <h3 className="mt-8 mb-3 text-base font-bold texto-base">Los que más compran</h3>
        <Tarjeta className="overflow-hidden">
          {ventas.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm texto-tenue">
              Cuando registres ventas aparecerá aquí el ranking.
            </p>
          ) : (
            <ul className="divide-y borde-suave">
              {rankingClientes(ventas)
                .slice(0, 6)
                .map((r, i) => (
                  <li key={r.clienteId}>
                    <Link
                      to={`/clientes/${r.clienteId}`}
                      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:superficie-hundida"
                    >
                      <span className="cifra w-5 shrink-0 text-[13px] font-bold texto-tenue">
                        {i + 1}
                      </span>
                      <Avatar nombre={r.nombre} tamano="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-semibold texto-base">
                          {r.nombre}
                        </span>
                        <span className="block truncate text-[12px] texto-suave">
                          {r.compras} {r.compras === 1 ? 'compra' : 'compras'} · última{' '}
                          {relativo(r.ultima)}
                        </span>
                      </span>
                      <span className="cifra shrink-0 text-[15px] font-bold texto-base">
                        {money(r.total)}
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </Tarjeta>

        <FormularioCliente abierto={nuevo} onCerrar={() => setNuevo(false)} />
      </Pagina>
    )
  }

  /* -------- Listado del grupo elegido -------- */
  const esEmpresa = vista === 'empresa'

  return (
    <Pagina>
      {!paraVenta && <SubNavVentas activa="clientes" />}

      {paraVenta ? (
        <button
          onClick={() => navegar(`/vender?tipo=${paraVenta}`)}
          className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold texto-suave transition-colors hover:texto-base"
        >
          <ArrowLeft className="size-4" /> Volver a la venta
        </button>
      ) : (
        <button
          onClick={() => {
            setVista(null)
            setTexto('')
          }}
          className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold texto-suave transition-colors hover:texto-base"
        >
          <ChevronRight className="size-4 rotate-180" /> Clientes
        </button>
      )}

      {paraVenta && (
        <p className="mb-4 rounded-2xl bg-bosque-50 px-4 py-3 text-[13px] font-medium text-bosque-700 dark:bg-bosque-500/10 dark:text-bosque-200">
          Elige {esEmpresa ? 'la empresa' : 'el particular'} al que le vendes. Se aplica el precio{' '}
          {esEmpresa ? 'mayorista' : 'al detalle'} que le corresponde.
        </p>
      )}

      <Encabezado
        titulo={esEmpresa ? 'Empresas' : 'Clientes particulares'}
        detalle={`${lista.length} ${lista.length === 1 ? 'registro' : 'registros'}`}
        accion={
          <div className="flex flex-wrap gap-2">
            {esAdmin && !paraVenta && (
              <Boton
                variante="secundario"
                icono={<FileSpreadsheet className="size-[18px]" />}
                onClick={() => void descargarExcel()}
              >
                Excel
              </Boton>
            )}
            <Boton icono={<UserPlus className="size-[18px]" />} onClick={() => setNuevo(true)}>
              Nuevo
            </Boton>
          </div>
        }
      />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={esEmpresa ? 'Nombre, RUT o comuna…' : 'Nombre, teléfono o comuna…'}
          className="h-12 w-full rounded-2xl border borde-base superficie-elevada pr-11 pl-11 text-[15px] texto-base transition-colors placeholder:texto-tenue focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10"
        />
        {texto && (
          <button
            onClick={() => setTexto('')}
            className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue hover:texto-base"
            aria-label="Borrar búsqueda"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <Vacio
          icono={<Users className="size-7" />}
          titulo={texto ? 'Nadie coincide con esa búsqueda' : 'Todavía no hay registros'}
          detalle={
            texto
              ? 'Prueba con otro nombre, RUT o comuna.'
              : esEmpresa
                ? 'Agrega la primera empresa para no volver a escribir sus datos.'
                : 'Agrega el primer cliente particular.'
          }
          accion={
            !texto ? (
              <Boton onClick={() => setNuevo(true)} icono={<UserPlus className="size-[18px]" />}>
                Nuevo cliente
              </Boton>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 escalonar sm:grid-cols-2 xl:grid-cols-3">
          {lista.map((c) => {
            const s = stats.get(c.id)
            const atiende = vendedorPorCliente.get(c.id)?.nombre
            return (
              <Link
                key={c.id}
                to={paraVenta ? `/vender?cliente=${c.id}` : `/clientes/${c.id}`}
                className="block"
              >
                <Tarjeta className="group h-full cursor-pointer p-4 transition-all duration-200 ease-[var(--ease-suave)] hover:-translate-y-0.5 hover:sombra-flotante">
                  <div className="flex items-start gap-3">
                    <Avatar nombre={c.nombre} />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-[15px] font-bold texto-base">
                        {[c.nombre, c.apellido].filter(Boolean).join(' ')}
                      </h3>
                      <p className="mt-0.5 truncate text-[12px] texto-suave">
                        {[c.comuna, c.telefono].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                      </p>
                      {esEmpresa && c.rubro && (
                        <Chip className="mt-2">
                          {RUBROS.find((r) => r.valor === c.rubro)?.etiqueta}
                        </Chip>
                      )}
                    </div>
                    <ChevronRight className="mt-1 size-4 shrink-0 texto-tenue transition-transform group-hover:translate-x-0.5" />
                  </div>

                  <div className="mt-3.5 flex items-end justify-between border-t borde-suave pt-3">
                    <div>
                      <p className="text-[11px] font-semibold texto-tenue uppercase">Compras</p>
                      <p className="cifra text-[15px] font-bold texto-base">{s?.compras ?? 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-semibold texto-tenue uppercase">
                        Total histórico
                      </p>
                      <p className="cifra text-[15px] font-bold texto-base">{money(s?.total ?? 0)}</p>
                    </div>
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] texto-suave">
                    <CalendarClock className="size-3 shrink-0" />
                    {s?.ultima ? `Última compra ${relativo(s.ultima)}` : 'Todavía no compra'}
                  </p>
                  {/* Quién lo atiende: para saber a quién preguntarle
                      por este cliente sin tener que abrir su ficha. */}
                  {atiende && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] texto-suave">
                      <UserRound className="size-3 shrink-0" />
                      Atiende <span className="font-semibold texto-base">{atiende}</span>
                    </p>
                  )}
                </Tarjeta>
              </Link>
            )
          })}
        </div>
      )}

      <FormularioCliente
        abierto={nuevo}
        onCerrar={() => setNuevo(false)}
        tipoInicial={vista}
        onGuardado={paraVenta ? (id) => navegar(`/vender?cliente=${id}`) : undefined}
      />
    </Pagina>
  )
}

function PuertaGrupo({
  titulo,
  detalle,
  cantidad,
  total,
  icono,
  tono,
  onClick,
}: {
  titulo: string
  detalle: string
  cantidad: number
  total: number
  icono: React.ReactNode
  tono: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex w-full min-w-0 items-center gap-4 overflow-hidden rounded-3xl bg-gradient-to-br p-4 text-left text-white sm:gap-5 sm:p-5',
        'transition-all duration-300 ease-[var(--ease-suave)] hover:-translate-y-1 hover:shadow-xl active:translate-y-0',
        tono,
      )}
    >
      <div
        className="absolute -top-16 -right-12 size-44 rounded-full bg-white/10 transition-transform duration-500 group-hover:scale-125"
        aria-hidden
      />

      {/* El logo define el alto de toda la tarjeta: no hay contenido
          apilado debajo de él, todo lo demás va a su derecha. */}
      <div className="relative grid size-24 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white/15 shadow-inner shadow-black/10 ring-1 ring-white/25 sm:size-28">
        {icono}
      </div>

      <div className="relative min-w-0 flex-1">
        <h3 className="truncate font-display text-xl font-bold sm:text-2xl">{titulo}</h3>
        <p className="mt-0.5 truncate text-[13px] text-white/75">{detalle}</p>
        <div className="mt-2.5 flex min-w-0 items-end gap-4">
          <div className="min-w-0 shrink-0">
            <p className="cifra font-display text-2xl font-bold sm:text-3xl">{cantidad}</p>
            <p className="truncate text-[11px] font-semibold text-white/70 uppercase">registros</p>
          </div>
          <div className="min-w-0">
            <p className="cifra truncate font-display text-base font-bold sm:text-lg">{money(total)}</p>
            <p className="truncate text-[11px] font-semibold text-white/70 uppercase">histórico</p>
          </div>
        </div>
      </div>

      <ChevronRight className="relative size-5 shrink-0 transition-transform group-hover:translate-x-1 sm:size-6" />
    </button>
  )
}
